/**
 * Authentication Service
 * Handles login, register, and user session management
 * SEPARATE from pre-registration API (which is for landing page only)
 */


import type {
  ApiResponse,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User,
} from '../types/api';
import { debug } from '../utils/debug';

interface PreRegisterRequest {
  name: string;
  email: string;
  telefone: string;
  areasInteresse: string[];
  comoConheceu: string;
  comoConheceuOutro?: string;
}

interface PaymentStatus {
  success: boolean;
  hasValidPayment: boolean;
  payment?: {
    id: string;
    status: string;
    paymentDate: string;
    confirmedDate: string;
    planName: string;
  };
  message?: string;
}
import { BaseService } from './BaseService';

export class AuthService extends BaseService {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {

    // In production, always make real API calls
    try {
      const response = await this.fetchApi<{ user: User; token: string }>(
        '/api/auth/login',
        {
          method: 'POST',
          body: JSON.stringify(credentials),
        }
      );

      // The backend returns the response in this format:
      // { success: true, data: { user: {...}, token: "..." }, message: "..." }
      if (response.success && response.data?.token) {
        this.storeToken(response.data.token);

        // Trigger storage event to notify other components
        debug.log('🔔 AuthService: Dispatching authStateChanged event after login');
        window.dispatchEvent(new Event('authStateChanged'));

        return {
          success: true,
          user: response.data.user,
          token: response.data.token,
          message: response.message || 'Login realizado com sucesso',
        };
      }

      return {
        success: false,
        message: response.message || 'Erro ao fazer login',
        error: response.error,
      };
    } catch (error) {
      debug.error('Login error details:', error);
      return {
        success: false,
        message: 'Erro de conexão. Verifique sua internet.',
        error: error instanceof Error ? error.message : 'Login failed',
      };
    }
  }



  /**
   * Get current user profile
   */
  async getCurrentUser(forceRefresh: boolean = false): Promise<ApiResponse<User>> {
    // Check if token exists before making API call
    if (!this.isAuthenticated()) {
      return this.createErrorResponse(
        'Usuário não autenticado.',
        'No authentication token found'
      );
    }

    // In production, always make real API calls
    try {
      return await this.fetchApi<User>('/api/auth/profile', {
        headers: this.getAuthHeaders(),
      }, forceRefresh ? 0 : 300000); // 5 minutes cache to prevent rate limiting
    } catch (error) {
      // If token is invalid, clear it
      if (error instanceof Error && error.message.includes('401')) {
        this.removeToken();
        window.dispatchEvent(new Event('authStateChanged'));
      }
      
      return this.createErrorResponse(
        'Erro ao carregar perfil do usuário.',
        error instanceof Error ? error.message : 'Profile fetch failed'
      );
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<ApiResponse<void>> {

    // In production, always make real API calls
    try {
      await this.fetchApi('/api/auth/logout', {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      this.removeToken();

      // Clear all API cache to prevent stale data
      this.clearAllCache();

      // Trigger storage event to notify other components
      window.dispatchEvent(new Event('authStateChanged'));

      return this.createSuccessResponse(
        undefined,
        'Logout realizado com sucesso'
      );
    } catch {
      // Always remove token locally, even if API call fails
      this.removeToken();

      // Clear all API cache even on error
      this.clearAllCache();

      // Trigger storage event to notify other components
      window.dispatchEvent(new Event('authStateChanged'));

      return this.createSuccessResponse(undefined, 'Logout realizado');
    }
  }

  /**
   * Check if user is currently authenticated (token exists)
   * Note: This only checks if token exists, not if it's valid
   * Use getCurrentUser() to validate token with backend
   */
  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }

  /**
   * Validate authentication with backend
   * Returns true if token is valid and user exists
   */
  async validateAuthentication(): Promise<boolean> {
    try {
      if (!this.isAuthenticated()) {
        return false;
      }

      const response = await this.getCurrentUser();
      
      if (response.success && response.data) {
        return true;
      } else {
        // Token is invalid or user doesn't exist
        this.clearAllAuthData();
        window.dispatchEvent(new Event('authStateChanged'));
        return false;
      }
    } catch (error) {
      debug.error('Auth validation failed:', error);
      this.clearAllAuthData();
      window.dispatchEvent(new Event('authStateChanged'));
      return false;
    }
  }

  /**
   * Clear all authentication data (useful for environment switches)
   */
  clearAllAuthData(): void {
    this.removeToken();
    this.clearAllCache(); // Clear all cached data
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    try {
      const response = await this.fetchApi<{ token: string }>(
        '/api/auth/refresh',
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
        }
      );

      if (response.success && response.data?.token) {
        this.storeToken(response.data.token);
      }

      return response;
    } catch (error) {
      return this.createErrorResponse(
        'Sessão expirada. Faça login novamente.',
        error instanceof Error ? error.message : 'Token refresh failed'
      );
    }
  }




  /**
   * Full registration with password (official app registration)
   */
  async register(data: RegisterRequest): Promise<ApiResponse<any>> {
    try {
      const registrationData = {
        name: data.name,
        email: data.email,
        password: data.password,
        telefone: data.telefone,
        areasInteresse: data.areasInteresse,
        comoConheceu: data.comoConheceu,
        comoConheceuOutro: data.comoConheceuOutro || undefined,
      };

      const response = await this.fetchApi<any>(
        '/api/auth/register',
        {
          method: 'POST',
          body: JSON.stringify(registrationData),
        }
      );

      // Store token if registration is successful
      if (response.success && response.data?.token) {
        this.storeToken(response.data.token);
      }

      return response;
    } catch (error) {
      return this.createErrorResponse(
        'Erro ao realizar cadastro.',
        error instanceof Error ? error.message : 'Registration failed'
      );
    }
  }

  /**
   * Pre-register user for promotional campaign (without password)
   */
  async preRegister(data: PreRegisterRequest): Promise<ApiResponse<any>> {
    try {
      // Use the real registration endpoint with correct field names
      const registrationData = {
        name: data.name,
        email: data.email,
        telefone: data.telefone,
        areasInteresse: data.areasInteresse,
        comoConheceu: data.comoConheceu,
        comoConheceuOutro: data.comoConheceuOutro || undefined,
      };

      return await this.fetchApi<any>(
        '/api/registration/register',
        {
          method: 'POST',
          body: JSON.stringify(registrationData),
        }
      );
    } catch (error) {
      return this.createErrorResponse(
        'Erro ao realizar cadastro.',
        error instanceof Error ? error.message : 'Registration failed'
      );
    }
  }

  /**
   * Check access state using the same endpoint as RootRoute
   */
  async checkAccessState(): Promise<{ hasFullAccess: boolean; hasPayment: boolean; emailVerified: boolean; isAuthenticated: boolean }> {
    try {
      if (!this.isAuthenticated()) {
        return {
          hasFullAccess: false,
          hasPayment: false,
          emailVerified: false,
          isAuthenticated: false
        };
      }

      const response = await this.fetchApi<any>('/api/auth/access-state', {
        headers: this.getAuthHeaders(),
      }, 0); // No cache - always fresh

      if (response.success && response.data) {
        const data = response.data;
        const hasFullAccess = data.shouldGoToDashboard || false;
        const hasPayment = data.hasActiveSubscription || data.hasPaidPrelaunchOffer || false;
        const emailVerified = data.emailVerified || false;

        return {
          hasFullAccess,
          hasPayment,
          emailVerified,
          isAuthenticated: true
        };
      }

      return {
        hasFullAccess: false,
        hasPayment: false,
        emailVerified: false,
        isAuthenticated: false
      };
    } catch (error) {
      debug.error('❌ AuthService: Error checking access state:', error);
      return {
        hasFullAccess: false,
        hasPayment: false,
        emailVerified: false,
        isAuthenticated: false
      };
    }
  }

  /**
   * Check if user has full access (authenticated + payment confirmed + email verified)
   */
  async checkFullAccess(): Promise<{ hasFullAccess: boolean; hasPayment: boolean; emailVerified: boolean; isAuthenticated: boolean }> {
    try {
      // First check if authenticated
      if (!this.isAuthenticated()) {
        return {
          hasFullAccess: false,
          hasPayment: false,
          emailVerified: false,
          isAuthenticated: false
        };
      }

      // Get user profile to check email verification
      const userResponse = await this.getCurrentUser();
      if (!userResponse.success || !userResponse.data) {
        return {
          hasFullAccess: false,
          hasPayment: false,
          emailVerified: false,
          isAuthenticated: false
        };
      }

      const emailVerified = userResponse.data.email_verified || false;

      // Check payment status - force fresh data to avoid cache issues
      const paymentStatus = await this.fetchApi<any>('/api/auth/payment-status', {
        headers: this.getAuthHeaders(),
      }, 0); // No cache - always fresh
      
      
      const hasPayment = paymentStatus.success && (paymentStatus.data?.hasValidPayment || false);

      const hasFullAccess = emailVerified && hasPayment;


      return {
        hasFullAccess,
        hasPayment,
        emailVerified,
        isAuthenticated: true
      };
    } catch (error) {
      debug.error('❌ AuthService: Error checking full access:', error);
      return {
        hasFullAccess: false,
        hasPayment: false,
        emailVerified: false,
        isAuthenticated: false
      };
    }
  }

  /**
   * Mark user onboarding as completed
   */
  async completeOnboarding(): Promise<ApiResponse<{ onboarding_completed: boolean }>> {
    try {
      const response = await this.fetchApi<{ onboarding_completed: boolean }>(
        '/api/auth/complete-onboarding',
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
        }
      );

      // Clear only user profile cache and trigger auth state change to refresh user data
      if (response.success) {
        // Clear only the user profile cache, not payment cache
        this.clearCacheByPattern(['/api/auth/profile']);
        window.dispatchEvent(new Event('authStateChanged'));
      }

      return response;
    } catch (error) {
      return this.createErrorResponse(
        'Erro ao completar onboarding.',
        error instanceof Error ? error.message : 'Complete onboarding failed'
      );
    }
  }

  /**
   * Check if authenticated user has validated payment
   */
  async checkPaymentStatus(): Promise<PaymentStatus> {
    try {
      // Use fetchApi with cache for payment status
      const response = await this.fetchApi<any>('/api/auth/payment-status', {
        headers: this.getAuthHeaders(),
      }, 300000); // 5 minutes cache to prevent rate limiting
      
      debug.log('💳 AuthService: Payment status response:', response);
      
      if (response.success) {
        // The backend returns payment status directly in the response, not nested in data
        return {
          success: true,
          hasValidPayment: response.data?.hasValidPayment || false,
          payment: response.data?.payment,
          message: response.message
        };
      }
      
      return {
        success: false,
        hasValidPayment: false,
        message: response.message || 'Payment status check failed'
      };
    } catch (error) {
      debug.error('❌ AuthService: Error checking payment status:', error);
      return {
        success: false,
        hasValidPayment: false,
        message: 'Error checking payment status'
      };
    }
  }


}

// Export singleton instance
export const authService = new AuthService();
